import { api } from "@/lib/api";

// Client-side error reporting.
//
// The ErrorBoundary already caught React RENDER crashes, which is the smallest
// slice of what actually breaks. The failures that lose a real user are mostly
// elsewhere: a click handler that throws, an await that rejects, a chunk that
// fails to download on a phone with two bars of signal. None of those hit a
// boundary, so the page just stops working and the person leaves.
//
// Reports are first-party (our own endpoint, no third party) and carry no user
// data — the message, the path and a truncated user-agent. That's enough to
// reproduce a bug and nothing more, which matters here because this product
// holds home locations and safeguarding material.

// One message can fire hundreds of times a second in a render loop. Reporting
// each one would flood the collection, hide everything else, and burn the
// rate limit — so identical messages are sent once per session.
const seen = new Set();
const MAX_PER_SESSION = 20;
let sent = 0;

export function reportClientError(message, kind = "error") {
  const msg = String(message || "").slice(0, 300);
  if (!msg || seen.has(msg) || sent >= MAX_PER_SESSION) return;
  seen.add(msg);
  sent += 1;
  api.reportClientError({
    message: msg,
    path: window.location.pathname,
    kind,
    ua: navigator.userAgent || "",
  }).catch(() => { /* reporting must never itself throw */ });
}

/** Attach global handlers. Called once from index.js. */
export function installErrorReporter() {
  window.addEventListener("error", (e) => {
    // Resource load failures (a dead <img>) surface here too and aren't worth
    // reporting — they have no `error` object and would drown the real ones.
    if (!e?.error && !e?.message) return;
    reportClientError(e.error?.message || e.message, "window");
  });

  window.addEventListener("unhandledrejection", (e) => {
    const r = e?.reason;
    // Axios failures are the noisy case: a 401 on a page you're not logged
    // into is normal life, not a defect. Only genuine transport/server
    // failures are worth waking anyone for.
    const status = r?.response?.status;
    if (status && status < 500) return;
    reportClientError(r?.message || String(r), "promise");
  });
}
